import { useSelector } from "react-redux"
import { isTenantIntegrationDisconnectEnabled } from "common/tenantIntegrationPermissionUtils"

/** Tenant portal id → super-admin panel id (same logical permission). */
const INTEGRATION_PERMISSION_ALIASES = {
  tenant_integrations_1: "sa_integrations_1",
  tenant_integrations_2: "sa_integrations_2_add",
  tenant_integrations_3: "sa_integrations_2_edit",
  tenant_integrations_4: "sa_integrations_4_integration",
  tenant_integrations_5: "sa_integrations_2_status",
}

const INTEGRATION_CATEGORY_IDS = new Set([48, 107])

/** Super-admin catalog only (no tenant equivalent). */
const SA_ONLY_INTEGRATION_PERMS = new Set(["sa_integrations_5"])

const integrationPermissionCandidates = permissionId => {
  const ids = new Set([permissionId])
  const sa = INTEGRATION_PERMISSION_ALIASES[permissionId]
  if (sa) ids.add(sa)
  Object.entries(INTEGRATION_PERMISSION_ALIASES).forEach(([tenantPerm, saPerm]) => {
    if (saPerm === permissionId) ids.add(tenantPerm)
  })
  if (permissionId === "tenant_integrations_2") {
    ids.add("sa_integrations_2")
    ids.add("sa_integrations_2_add")
  }
  if (permissionId === "tenant_integrations_3") {
    ids.add("sa_integrations_2")
    ids.add("sa_integrations_3")
    ids.add("sa_integrations_2_edit")
    ids.add("sa_integrations_3_edit")
  }
  if (permissionId === "tenant_integrations_4") {
    ids.add("sa_integrations_2")
    ids.add("sa_integrations_4")
    ids.add("sa_integrations_4_integration")
  }
  if (permissionId === "tenant_integrations_5") {
    ids.add("sa_integrations_2")
    ids.add("sa_integrations_2_status")
  }

  if (permissionId === "sa_integrations_2") {
    ids.add("sa_integrations_2_add")
    ids.add("sa_integrations_2_edit")
    ids.add("sa_integrations_2_status")
  }
  if (permissionId === "sa_integrations_3") {
    ids.add("sa_integrations_3_add")
    ids.add("sa_integrations_3_edit")
  }
  if (permissionId === "sa_integrations_4") {
    ids.add("sa_integrations_4_integration")
    ids.add("sa_integrations_4_category")
  }

  return [...ids]
}

/**
 * Hook to check role-based permissions for the current user.
 *
 * @returns {{ hasPermission: Function, hasIntegrationPermission: Function,
 *             hasAnyPermission: Function, isSuperAdmin: boolean, userRole: object }}
 */
const usePermissions = () => {
  const { userRole, accountType, accountScope } = useSelector(state => state.Login || {})

  const isSuperAdmin =
    accountScope === "super_admin" || accountType === "superadmin"

  /**
   * Per-integration permission map from the saved role.
   * Shape: { [integrationId]: { tenant_integrations_1: bool, ... } }
   * Populated from role.integrationPermissions on login / userDetails.
   */
  const integrationPermissionsMap = userRole?.integrationPermissions || {}

  /**
   * Check if user has a specific permission (module-level).
   * @param {number|string} categoryId - The permission category ID
   * @param {string} permissionId - The specific permission ID
   * @returns {boolean}
   */
  const hasGranularTenantIntegrationPermission = permissionId => {
    const map = integrationPermissionsMap
    if (!map || typeof map !== "object") return false
    const candidates = integrationPermissionCandidates(permissionId)
    return Object.values(map).some(entry => {
      if (!entry || typeof entry !== "object") return false
      return candidates.some(pid => entry[pid] === true)
    })
  }

  const hasPermission = (categoryId, permissionId) => {
    if (isSuperAdmin) return true
    if (!userRole) return false

    const catNum = Number(categoryId)
    if (INTEGRATION_CATEGORY_IDS.has(catNum)) {
      const permissionGroups = Array.isArray(userRole.permissions)
        ? userRole.permissions
        : []
      if (SA_ONLY_INTEGRATION_PERMS.has(permissionId)) {
        const category = permissionGroups.find(r => Number(r.id) === 107)
        const perm = category?.permissions?.find(p => p.id === permissionId)
        return !!(perm && perm.isEnabled)
      }
      if (permissionId === "tenant_integrations_5") {
        if (isTenantIntegrationDisconnectEnabled(userRole)) return true
      }
      const candidates = integrationPermissionCandidates(permissionId)
      for (const icat of INTEGRATION_CATEGORY_IDS) {
        const category = permissionGroups.find(r => Number(r.id) === icat)
        if (!category?.permissions) continue
        const perm = category.permissions.find(p => candidates.includes(p.id))
        if (perm && perm.isEnabled) return true
      }
      // Role form stores Integrations as per-catalogue grants (no category 48).
      if (hasGranularTenantIntegrationPermission(permissionId)) {
        return true
      }
      return false
    }

    if (!userRole?.permissions) return false

    const category = userRole.permissions.find(r => r.id === categoryId)
    if (!category) return false

    const alias = INTEGRATION_PERMISSION_ALIASES[permissionId]
    const perm = category.permissions?.find(
      p =>
        p.id === permissionId ||
        (alias && p.id === alias)
    )
    return !!(perm && perm.isEnabled)
  }

  /**
   * Check if the current user has a specific action permission for a given integration.
   *
   * Enforcement logic (AND):
   *  1. Module-level check (category 48): the action must be enabled at the role level.
   *  2. Per-integration check: if role.integrationPermissions has an entry for this
   *     integration, the specific permission key must also be true.
   *     If NO entry exists, the module-level result is used as-is (not restricted).
   *
   * @param {string} integrationId - The integration catalogue _id
   * @param {string} permissionId  - e.g. "tenant_integrations_1"
   * @returns {boolean}
   */
  const hasIntegrationPermission = (integrationId, permissionId) => {
    if (isSuperAdmin) return true
    const intId = String(integrationId || "")
    const perIntegration = intId ? integrationPermissionsMap[intId] : null
    // Explicit per-catalogue entry is authoritative (new role UI).
    if (perIntegration && typeof perIntegration === "object") {
      return perIntegration[permissionId] === true
    }
    // Legacy: no map entry → category-level Integrations permissions only.
    return hasPermission(48, permissionId)
  }

  /**
   * Check if user has any permission in a category.
   * @param {number|string} categoryId - The permission category ID
   * @returns {boolean}
   */
  const hasAnyPermission = categoryId => {
    if (isSuperAdmin) return true
    if (!userRole?.permissions) return false

    const category = userRole.permissions.find(r => r.id === categoryId)
    if (!category) return false

    return category.permissions?.some(p => p.isEnabled) ?? false
  }

  return {
    hasPermission,
    hasIntegrationPermission,
    hasAnyPermission,
    isSuperAdmin,
    userRole,
  }
}

export default usePermissions
